import { ReactNode, forwardRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@paulownia/shared';

interface PullToRefreshProps {
  children: ReactNode;
  pullDistance: number;
  isRefreshing: boolean;
  isPulling: boolean;
  threshold?: number;
  className?: string;
}

export const PullToRefresh = forwardRef<HTMLDivElement, PullToRefreshProps>(
  (
    {
      children,
      pullDistance,
      isRefreshing,
      isPulling,
      threshold = 80,
      className,
    },
    ref
  ) => {
    // Calculate rotation based on pull distance
    const rotation = Math.min((pullDistance / threshold) * 360, 360);
    const opacity = Math.min(pullDistance / (threshold * 0.5), 1);
    const scale = Math.min(0.5 + (pullDistance / threshold) * 0.5, 1);
    const isThresholdReached = pullDistance >= threshold;

    return (
      <div
        ref={ref}
        className={cn(
          'relative h-full overflow-y-auto overflow-x-hidden overscroll-none',
          className
        )}
      >
        {/* Pull indicator */}
        <div
          className={cn(
            'absolute left-1/2 -translate-x-1/2 z-50 flex items-center justify-center transition-opacity',
            (isPulling || isRefreshing) ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            top: Math.max(pullDistance - 40, 8),
            transition: isPulling ? 'none' : 'top 0.3s ease-out, opacity 0.2s ease-out',
          }}
        >
          <div
            className={cn(
              'flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200',
              isThresholdReached && !isRefreshing && 'border-orange-400 bg-orange-50'
            )}
            style={{
              opacity,
              transform: `scale(${scale})`,
              transition: isPulling ? 'none' : 'transform 0.3s ease-out',
            }}
          >
            <RefreshCw
              className={cn(
                'w-5 h-5 text-gray-600',
                isThresholdReached && !isRefreshing && 'text-orange-500',
                isRefreshing && 'animate-spin text-orange-500'
              )}
              style={{
                transform: isRefreshing ? undefined : `rotate(${rotation}deg)`,
                transition: isPulling && !isRefreshing ? 'none' : 'transform 0.1s linear',
              }}
            />
          </div>
        </div>

        {/* Content container with transform for pull effect */}
        {/* Only apply transform when actually pulling to avoid breaking fixed positioning */}
        <div
          style={pullDistance > 0 ? {
            transform: `translateY(${pullDistance}px)`,
            transition: isPulling ? 'none' : 'transform 0.3s ease-out',
          } : undefined}
        >
          {children}
        </div>
      </div>
    );
  }
);

PullToRefresh.displayName = 'PullToRefresh';
